import { ListItemProps } from "@/components/ListItem/ListItem";
import { toFormat } from "@/lib/dateHelpers";
import { Address, Employment, BackofficePerson, PhoneNumber, Band } from "@/types";
import { capitalize } from "lodash";
import { useNavigate } from "@/router";
import { getAddressIconName, getAddressTypeLabel } from "@/lib/addressHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import { buildEmailHeadline } from "@/features/person/utils/utils";

type NavigateFunction = ReturnType<typeof useNavigate>;

export function phoneOptions(phoneNumbers: PhoneNumber[] | undefined, alternatePhone?: string): ListItemProps[] {
  const phoneNumbersList =
    !phoneNumbers || phoneNumbers.length === 0
      ? [
          {
            phoneFormatted: null,
            phoneType: "mobile"
          }
        ]
      : phoneNumbers;

  const formattedAlternatePhone = alternatePhone ? formatPhoneNumber(alternatePhone) : undefined;

  // Check if alternatePhone is different from any existing phone numbers
  const shouldShowAlternatePhone =
    formattedAlternatePhone &&
    !phoneNumbersList.some(
      (phone) => phone.phoneFormatted === formattedAlternatePhone || phone.phoneFormatted === alternatePhone
    );

  // Sort phone numbers to show mobile first
  const sortedPhoneNumbers = [...phoneNumbersList].sort((a, b) => {
    if (a.phoneType === "mobile") return -1;
    if (b.phoneType === "mobile") return 1;
    return 0;
  });

  const phoneOptions = sortedPhoneNumbers.map((phoneNumber) => ({
    iconName: "call" as const,
    iconBackground: true,
    headline: phoneNumber.phoneFormatted || "--",
    overline: capitalize(phoneNumber.phoneType),
    copyable: !!phoneNumber.phoneFormatted,
    copyText: phoneNumber.phoneFormatted || undefined
  }));

  // Add alternate phone at the top if it's different
  if (shouldShowAlternatePhone) {
    phoneOptions.unshift({
      iconName: "call" as const,
      iconBackground: true,
      headline: formattedAlternatePhone,
      overline: "Application Phone",
      copyable: true,
      copyText: formattedAlternatePhone
    });
  }

  return phoneOptions;
}

export function emailOptions(email: string | undefined, alternateEmail?: string): ListItemProps[] {
  const trimmedEmail = email?.trim();
  const emailAddress = trimmedEmail ? trimmedEmail : null;

  // Check if alternateEmail is different from profile email
  const trimmedAlternateEmail = alternateEmail?.trim();
  const shouldShowAlternateEmail = trimmedAlternateEmail && trimmedAlternateEmail !== trimmedEmail;

  const emailOptions: ListItemProps[] = [
    {
      iconName: "mail" as const,
      iconBackground: true,
      headline: buildEmailHeadline(emailAddress),
      copyable: !!emailAddress,
      copyText: emailAddress || undefined,
      copyFieldLabel: "Email"
    }
  ];

  // Add alternate email at the top if it's different
  if (shouldShowAlternateEmail) {
    emailOptions.unshift({
      iconName: "mail" as const,
      iconBackground: true,
      overline: "Application Email",
      headline: buildEmailHeadline(trimmedAlternateEmail),
      copyable: true,
      copyText: trimmedAlternateEmail
    });
  }

  return emailOptions;
}

export function addressOptions(addresses: Address[] | undefined): ListItemProps[] {
  const addressList =
    !addresses || addresses.length === 0
      ? [
          {
            addressType: "home" as const,
            address1: null,
            address2: null,
            city: null,
            state: null,
            postalCode: null
          }
        ]
      : addresses;

  // Sort addresses to show home address first
  const sortedAddresses = [...addressList].sort((a, b) => {
    if (a.addressType === "home") return -1;
    if (b.addressType === "home") return 1;
    return 0;
  });

  return sortedAddresses.map((address) => {
    const { address1, address2, city, state, postalCode } = address;
    const addressLine = address1
      ? `${address1}${address2 ? `, ${address2}` : ""}, ${city}, ${state} ${postalCode}`
      : undefined;
    return {
      iconName: getAddressIconName(address.addressType),
      overline: getAddressTypeLabel(address.addressType),
      iconBackground: true,
      copyable: !!addressLine,
      copyText: addressLine,
      headline: address1 ? (
        <>
          <span>
            {address1}
            {address2 ? `, ${address2}` : ""}
          </span>
          <br />
          <span>{`${city}, ${state} ${postalCode}`}</span>
        </>
      ) : (
        "--"
      )
    };
  });
}

export function employmentOptions(
  employments: Employment[],
  _navigate?: NavigateFunction,
  _pgaId?: string,
  maxItems: number = 1
): ListItemProps[] {
  if (!employments || employments.length === 0) {
    return [];
  }

  // Sort by startedOn date (newest to oldest), with null dates last
  const sortedEmployments = [...employments].sort((a, b) => {
    const aDate = a.startedOn ? new Date(a.startedOn).getTime() : 0;
    const bDate = b.startedOn ? new Date(b.startedOn).getTime() : 0;
    return bDate - aDate; // Descending order (newest first)
  });

  // By default, show only current employment (endedOn is null)
  // If maxItems > 1, show up to maxItems regardless of current status
  const filteredEmployments =
    maxItems === 1 ? sortedEmployments.filter((emp) => !emp.endedOn).slice(0, 1) : sortedEmployments.slice(0, maxItems);

  // If no current employment found and maxItems is 1, show the most recent employment
  const employmentList =
    filteredEmployments.length === 0 && maxItems === 1 ? sortedEmployments.slice(0, 1) : filteredEmployments;

  return employmentList.map((employment) => {
    const startDate = employment.startedOn ? toFormat(employment.startedOn) || "Unknown start" : "Unknown start";
    const endDate = employment.endedOn ? toFormat(employment.endedOn) || "Unknown end" : "Present";
    const dateRange = `${startDate} - ${endDate}`;

    // Build headline with job classification in parentheses
    const title = employment.formattedTitle || employment.title || "No title";
    const headline = employment.jobClassification ? `${title} (${employment.jobClassification})` : title;

    return {
      iconName: "store",
      iconBackground: true,
      headline,
      overline: employment.facility?.name || "Unknown facility",
      supportingText: dateRange
    };
  });
}
export function facilityOptions(
  employment: Employment | null | undefined,
  _navigate?: NavigateFunction
): ListItemProps[] {
  let details: ListItemProps[] = [];

  if (employment?.facility) {
    details.push({
      iconName: "location_on",
      iconBackground: true,
      overline: "Facility Name",
      headline: employment.facility.name,
      action: employment.facility.id
        ? () => window.open(`/facilities/${employment.facility.id}/details/contact`, "_blank")
        : undefined,
      actionIcon: employment.facility.id ? "navigate_next" : undefined
    });
  }
  return details;
}

export function membershipOptions(band: Band | undefined): ListItemProps[] {
  const primaryBand = !band
    ? {
        section: {
          name: null
        },
        expiresOn: "--",
        startedOn: "--"
      }
    : band;

  let details: ListItemProps[] = [];

  if (!!primaryBand?.section) {
    details.push({
      iconName: "location_on",
      iconBackground: true,
      overline: "Section",
      headline: primaryBand.section.name
    });
    details.push({
      iconName: "calendar_today",
      iconBackground: true,
      overline: "Expiration",
      headline: toFormat(primaryBand.expiresOn)
    });
    details.push({
      iconName: "calendar_today",
      iconBackground: true,
      overline: "Start Date",
      headline: toFormat(primaryBand.startedOn)
    });
  }
  return details;
}

export function programOptions(person: BackofficePerson, universityName?: string): ListItemProps[] {
  let details: ListItemProps[] = [];

  // Get the program type from latestProgram
  const programType = person?.latestProgram?.programType?.name;
  const classification = person?.latestProgram?.band?.classification?.name;

  // If university name is provided, show it instead of section (for student contexts)
  if (universityName) {
    details.push({
      iconName: "school",
      iconBackground: true,
      overline: programType || "Student",
      headline: universityName
    });
  } else if (programType && person?.primarySection?.name) {
    // Show primary section with program type overline only if programType exists
    details.push({
      iconName: "badge",
      iconBackground: true,
      overline: classification ? `${programType} (${classification})` : programType,
      headline: person.primarySection.name
    });
  }

  return details;
}

export function groupOptions(person: BackofficePerson): ListItemProps[] {
  let details: ListItemProps[] = [];

  if (person?.groupAssignments && person.groupAssignments.length > 0) {
    // Filter for active groups based on expiration date
    const now = new Date();
    const activeGroups = person.groupAssignments.filter((assignment) => {
      const expiresAt = new Date(assignment.expiresAt);
      return expiresAt > now;
    });

    if (activeGroups.length > 0) {
      const groupNames = activeGroups.map((assignment) => assignment.group.name).join(", ");

      details.push({
        iconName: "groups",
        iconBackground: true,
        overline: "Active Groups",
        headline: groupNames
      });
    }
  }

  return details;
}

export function jobApplicationOptions(person: BackofficePerson, _navigate?: NavigateFunction): ListItemProps[] {
  let details: ListItemProps[] = [];

  if (person?.totalActiveJobApplications !== undefined) {
    details.push({
      iconName: "business_center",
      iconBackground: true,
      overline: "Active Applications",
      headline: `${person.totalActiveJobApplications}`
    });
  }

  if (person?.totalJobApplications !== undefined) {
    details.push({
      iconName: "business_center",
      iconBackground: true,
      overline: "Total Applications",
      headline: `${person.totalJobApplications}`
    });
  }

  return details;
}

export function personalOptions(person: BackofficePerson): ListItemProps[] {
  const primaryPerson = !person
    ? {
        age: null,
        gender: null,
        informalName: null,
        dob: null
      }
    : person;
  let details: ListItemProps[] = [];

  // Always show Date of Birth
  const dateOfBirth = primaryPerson.dob
    ? toFormat(
        `${primaryPerson.dob.year}-${String(primaryPerson.dob.month).padStart(2, "0")}-${String(
          primaryPerson.dob.day
        ).padStart(2, "0")}`
      ) || "--"
    : "--";

  details.push({
    iconName: "cake",
    iconBackground: true,
    overline: "Date of Birth",
    headline: dateOfBirth
  });

  // Always show Age
  details.push({
    iconName: "person",
    iconBackground: true,
    overline: "Age",
    headline: primaryPerson.age ? `${primaryPerson.age}` : "--"
  });

  // Always show Gender
  const gender = {
    F: "Female",
    M: "Male",
    O: "Other"
  };
  const genderHeadline = primaryPerson.gender
    ? gender[primaryPerson.gender as keyof typeof gender] || "Not specified"
    : "--";

  details.push({
    iconName: "person",
    iconBackground: true,
    overline: "Gender",
    headline: genderHeadline
  });

  // Always show Preferred First Name
  details.push({
    iconName: "person",
    iconBackground: true,
    overline: "Preferred First Name",
    headline: primaryPerson.informalName || "--"
  });

  return details;
}
